import React, {useEffect, useCallback, useMemo, useState} from "react"
import {useCookies} from "react-cookie"
import styled from "styled-components"
/* UI parts */
import {
  Root,
  getHeader,
  getFooter,
  getDrawerSidebar,
  getSidebarTrigger,
  getSidebarContent,
  getCollapseBtn,
  getContent,
  getDefaultScheme,
  getFixedScheme,
  getStandardScheme,
  getContentBasedScheme,
  getCozyScheme,
  getMuiTreasuryScheme,
  getCollapseIcon,
} from "@mui-treasury/layout"
import {Container, Toolbar} from "@material-ui/core"
import {MuiPickersUtilsProvider} from "@material-ui/pickers"
import {orange} from "@material-ui/core/colors"
import CssBaseline from "@material-ui/core/CssBaseline"
import {ThemeProvider, makeStyles} from "@material-ui/core/styles"
/* Locale */
import {useTranslation} from "react-i18next"
import {ja, enUS} from "date-fns/locale"
/* Others */
import {useTheme} from "./hooks"
import {ConfirmDialog, LoadingModal, Guide, GuideDialog, PerspectiveBar} from "./components"
import DateFnsUtilsEx from "./classes/DateFnsUtilsEx"
import {useStore, useDispatch} from "./Store"
import {HeaderEx, NavHeaderEx, NavContentEx, ContentEx, FooterEx} from "./templates"
import {
  isSingleUser,
  getSingleToken,
  getTokenCookieName,
  getTokenCookie,
  getUserCookieName,
  getUserCookie,
  getCookieOptions,
  isAmOne,
  redirectToDashboard,
} from "./common"

const Header = getHeader(styled)
const Footer = getFooter(styled)
const DrawerSidebar = getDrawerSidebar(styled)
const SidebarTrigger = getSidebarTrigger(styled)
const SidebarContent = getSidebarContent(styled)
const CollapseBtn = getCollapseBtn(styled)
const Content = getContent(styled)
const CollapseIcon = getCollapseIcon(styled)
const defaultScheme = getDefaultScheme()
const fixedScheme = getFixedScheme()
const standardScheme = getStandardScheme()
const contentBaseScheme = getContentBasedScheme()
const cozyScheme = getCozyScheme()
const muitreasuryScheme = getMuiTreasuryScheme()

const useStyles = makeStyles((theme) => ({
  header: {
    height: `64px !importnt`,
    backgroundColor: (props) => props.theme.palette.background.default,
  },
  toolbar: {
    marginTop: 16,
    // marginBottom: 16,
    // paddingLeft: 12,
  },
  iconButton: {
    marginRight: 16,
  },
  collapse: {
    color: (props) => props.theme.palette.primary.contrastText,
    backgroundColor: (props) => props.theme.palette.background.default,
    "&:hover": {
      backgroundColor: orange.A400,
    },
    height: 56,
  },
  container: {
    margin: 0,
    padding: `0px 16px`,
    maxWidth: `100% !important`,
  },
  footer: {
    padding: 0,
  },
}))

const sidebarId1 = `primarySidebar`

const App = () => {
  const {t, i18n} = useTranslation()
  const {dispatch} = useDispatch()
  const {
    store: {page, themeColor, locale, preset, showHeader, showFooter, showNav, sidebarWidth, userInfo, initialized},
  } = useStore()

  const [cookies, setCookie] = useCookies([
    getTokenCookieName(),
    getUserCookieName(),
    `milizeLayoutPreset`,
    `milizeSidebarWidth`,
    `milizeThemeColor`,
    `milizeLocale`,
    `milizeShowHeader`,
    `milizeShowNav`,
    `milizeShowFooter`,
  ])

  const theme = useTheme(themeColor)
  const classes = useStyles({theme})
  const [fix] = useState(false)
  const [onLoad, setOnLoad] = useState(false)
  const [onSetupCoookies, setOnSetupCookies] = useState(false)

  const initialState = {
    sidebar: {
      [sidebarId1]: {collapsed: true}, // 初回起動時、サイドメニューは表示させるが、パネルは閉じる
    },
  }

  /** ローディングダイアログ表示 */
  const showLoadingDialog = useCallback(() => {
    const loadingModal = {
      title: t(`welcome.title`, {
        message1: t(`welcome.message`),
        message2: process.env.APP_NAME,
        joinArrays: ``,
      }),
      message: t(`loadingInitialData`),
      state: true,
    }
    dispatch({
      type: `LOADING_MODAL`,
      loadingModal,
    })
  })

  const presets = {
    createDefaultLayout: defaultScheme,
    createFixedLayout: fixedScheme,
    createStandardLayout: standardScheme,
    createContentBasedLayout: contentBaseScheme,
    createCustomLayout: standardScheme,
    createCozyLayout: cozyScheme,
    createMuiTreasuryLayout: muitreasuryScheme,
  }

  const scheme = presets[preset] ?? standardScheme

  useEffect(() => {
    scheme.configureHeader((builder) => {
      console.log(builder)
      const breakpoints = [`xs`, `sm`, `md`, `lg`, `xl`]
      breakpoints.map((breakpoint) =>
        builder.registerConfig(breakpoint, {
          position: `sticky`,
          initialHeight: 88,
        }),
      )
    })
    scheme.configureEdgeSidebar((builder) => {
      builder.update(sidebarId1, (config) => {
        if (config?.xs?.width ?? null) config.xs.width = `100%`
        const breakpoints = [`sm`, `md`, `lg`, `xl`]
        breakpoints.map((breakpoint) => {
          if (config?.[breakpoint]?.width ?? null) {
            config[breakpoint].width = Number(sidebarWidth)
          }
        })
      })
    })
  }, [scheme, sidebarWidth, fix])

  useEffect(() => {
    const fetch = async () => {
      try {
        var token = getTokenCookie(cookies)
        if (!token) await redirectToDashboard()

        if (isSingleUser()) {
          if (token !== getSingleToken()) await redirectToDashboard()
          setCookie(getTokenCookieName(), getSingleToken(), getCookieOptions())
        } else  {
          const userCookie = getUserCookie(cookies)
          console.log(`userCookie=`,userCookie)
          if (!userCookie) await redirectToDashboard()

          const userInfo = typeof userCookie === `string` ? JSON.parse(userCookie) : userCookie
          console.log(`userCookieInfo=`,userInfo)
          dispatch({
            type: `USER_INFO`,
            userInfo,
          })
        }
      } catch (error) {
        console.log(error)
      }
    }

    if (onLoad)
      fetch()
  }, [onLoad])
  
  useEffect(() => {
    const fetch = async () => {
      setOnSetupCookies(true)

      // ロケール変更及びローディングダイアログ表示
      // (ログイン情報確認時)
      await i18n.changeLanguage(locale)
      if (!initialized)
        showLoadingDialog()

      const update = (name, type, key, extractor, callback) => {
        const value = extractor(cookies[name])
        dispatch({
          type,
          [key]: value,
        })
        // 設定の期限を延長するために再設定
        setCookie(name, value, getCookieOptions())
        if (callback) callback(value)
      }
      const setupCookieSetting = (
        name,
        type,
        key,
        extractor,
        isUpdate = (cookies, name) => name in cookies,
        callback = undefined,
      ) => {
        if (isUpdate(cookies, name)) {
          update(name, type, key, extractor, callback)
        }
      }
      setupCookieSetting(
        `milizeLocale`,
        `LOCALE`,
        `locale`,
        (value) => value || `ja`,
        () => true,
        (value) => i18n.changeLanguage(value),
      )
      setupCookieSetting(`milizeLayoutPreset`, `PRESET`, `preset`, (value) => value)
      setupCookieSetting(`milizeSidebarWidth`, `SIDEBAR_WIDTH`, `sidebarWidth`, (value) => value)
      setupCookieSetting(`milizeThemeColor`, `THEME_COLOR`, `themeColor`, (value) => value)
      setupCookieSetting(`milizeShowHeader`, `SHOW_HEADER`, `showHeader`, (value) => value !== `false`)
      setupCookieSetting(`milizeShowFooter`, `SHOW_FOOTER`, `showFooter`, (value) => value !== `false`)
      setupCookieSetting(`milizeShowNav`, `SHOW_NAV`, `showNav`, (value) => value !== `false`)

      setOnSetupCookies(false)
    }

    if (onLoad && !onSetupCoookies)
      fetch()
  }, [cookies])

  //if (debug) scheme.debug()   // Nakamura Memo : コンソールへの表示が邪魔なので、一時的にコメントアウト

  /***** コンテンツ *****/

  /** ヘッダラッパー */
  const headerContainer = useMemo(() => {
    if (!showHeader)
      return null

    console.log(`Current Page: `, page)

    // Nakamura Memo : スクロール時にコンテンツがヘッダ（の Tabs）にかぶるのを防止するために高さを固定する。
    //                 レイアウトによってはおかしくなるかもしれないが、ＰＣで奇麗になるように暫定処置。
    const headerHeight = 128

    const bar = page === `SETTING` ? null : <PerspectiveBar />
    return (
      <Header className={classes.header} style={{ height: headerHeight}} id={`similarchart_header`}>
        <Toolbar className={classes.toolbar}>
          <SidebarTrigger sidebarId={sidebarId1} />
          <CollapseIcon sidebarId={sidebarId1} className={classes.iconButton} />
          <HeaderEx />
        </Toolbar>
        {bar}
      </Header>
    )
  }, [page, showHeader])

  /** サイドバーラッパー */
  const sidebarContainer = useCallback((sidebar, setOpen, setCollapsed) => {
      if (!showNav)
        return null

      const navHeader = <NavHeaderEx collapsed={sidebar[sidebarId1]?.collapsed ?? true} />
      const navContent = <NavContentEx id={sidebarId1} sidebar={sidebar} setOpen={setOpen} setCollapsed={setCollapsed} />
      return (
        <DrawerSidebar sidebarId={sidebarId1}>
          <SidebarContent>
            {navHeader}
            {navContent}
          </SidebarContent>
          <CollapseBtn sidebarId={sidebarId1} className={classes.collapse} />
        </DrawerSidebar>
      )
    },
    [showNav, sidebarWidth],
  )

  /** コンテンツラッパー */
  const contentContainer = useMemo(() => {
      if (!classes)
        return null

      return (
        <Content>
          <MuiPickersUtilsProvider utils={DateFnsUtilsEx} locale={locale === `ja` ? ja : enUS}>
            <Container className={classes.container}>
              <ContentEx />
            </Container>
          </MuiPickersUtilsProvider>
        </Content>
      )
    },
    [locale],
  )

  /** フッターラッパー */
  const footerContainer = useMemo(() => {
    if (isAmOne() || !showFooter)
      return null

    return (
      <Footer className={classes.footer} id={`similarchart_footer`}>
        <FooterEx />
      </Footer>
    )
  }, [showFooter])

  /** アプリケーション実体 */
  const view = useMemo(() => {
    if (!onLoad)
      return null

    return (
        <Root initialState={initialState} scheme={scheme}>
          {({state: {sidebar}, setOpen, setCollapsed}) => (
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <GuideDialog />
              <Guide />
              <ConfirmDialog />
              <LoadingModal />
              {headerContainer}
              {sidebarContainer(sidebar, setOpen, setCollapsed)}
              {contentContainer}
              {footerContainer}
            </ThemeProvider>
          )}
        </Root>
      )
    },
    [scheme, onLoad, theme, locale, page, showHeader, showNav, showFooter, sidebarWidth],
  )

  /** ユーザ情報読込画面 */
  const loading = useMemo(() => {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoadingModal />
      </ThemeProvider>
    )
  }, [themeColor])

  /** 縦スクロール位置保存 */
  const onWindowScroll = useCallback(() => {
    dispatch({
      type: `SCROLL_Y`,
      value: window.scrollY,
    })
  }, [])

  /* Cookies情報初期設定 */
  useEffect(() => {
    const fetch = async () => {
      if (!cookies[`milizeSidebarWidth`])
        setCookie(`milizeSidebarWidth`, sidebarWidth, getCookieOptions())

      let thc = cookies[`milizeThemeColor`]
      dispatch({
        type: `THEME_COLOR`,
        themeColor: thc ?? `dark`,
      })
      if (!thc)
        setCookie(`milizeThemeColor`, `dark`, getCookieOptions())

      let loc = cookies[`milizeLocale`]
      await i18n.changeLanguage(loc ?? `ja`)
      if (loc)
        dispatch({
          type: `LOCALE`,
          locale: loc,
        })
      else
        setCookie(`milizeLocale`, `ja`, getCookieOptions())

      showLoadingDialog()
      setOnLoad(true)
    }

    if (!onLoad)
      fetch()

    document.addEventListener('scroll', onWindowScroll)
    return () => document.removeEventListener('scroll', onWindowScroll)
  }, [])

  return userInfo ? view : loading
}

export default process.env.NODE_ENV === `development` ? require(`react-hot-loader`).hot(module)(App) : App
